import { ContentApiDto, OrderApiDto } from '@b2x/storefront-api-js-client/src';
import { OrderPopulate } from '@b2x/storefront-api-js-client/src/orders';
import classnames from 'classnames';
import React from 'react';

import { useCustomerApi } from './api/useCustomerApi';
import { useOrdersApi } from './api/useOrdersApi';
import { useAppContext } from './AppContext';
import { ContactUsContentType } from './contentTypes';
import { CustomerCareForm } from './form/CustomerCareForm';
import { Radio } from './form/fields/RadioCheck';
import { Select } from './form/fields/Select';
import {
  Form,
  formikBooleanAsString,
  formikString,
  getFormikBooleanAsStringValue,
  getInitialBooleanAsString,
  getInitialString,
} from './form/Form';
import { FormGroup } from './form/FormGroup';
import { OrderGuestForm } from './form/OrderGuestForm';
import { Div, P } from './HTMLElement';
import { t } from './i18n/i18n';
import { OrderDetails } from './OrderDetails';
import { useSearchParams } from './router/router';
import { useContent } from './useContent';
import { createUUID, formatHtml } from './util';

export interface ContactUsProps {
  disableSatisfiedForm?: boolean;
  showSatisfiedForm?: boolean;
}

export const ContactUs = ({ disableSatisfiedForm, showSatisfiedForm }: ContactUsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const content = useContent<ContactUsContentType>('CONTACT_US_CONTENT');

  const [selectedSubjectIndex, setSelectedSubjectIndex] = React.useState<number>();
  const [customerOrders, setCustomerOrders] = React.useState<Array<OrderApiDto>>();
  const [selectedCustomerOrderIndex, setSelectedCustomerOrderIndex] = React.useState<number>();
  const [order, setOrder] = React.useState<OrderApiDto>();
  const [satisfied, setSatisfied] = React.useState<boolean>(true);
  const [forceCustomerCareForm, setForceCustomerCareForm] = React.useState<boolean>(false);

  const [isRightOfWithdrawal, setIsRightOfWithdrawal] = React.useState<boolean>(false);

  const { session } = useAppContext();
  const { getOrders } = useCustomerApi();
  const { getOrderByCodeAndEmail } = useOrdersApi();

  const handleSubjectFormChange = React.useCallback(
    (values: SubjectFormValues) => {
      setSearchParams({});
      setSelectedCustomerOrderIndex(undefined);

      if (values.subject === '') {
        setSelectedSubjectIndex(undefined);
      } else {
        setSelectedSubjectIndex(Number(values.subject));
      }
      setOrder(undefined);
      setSatisfied(true);
    },
    [setSearchParams],
  );

  const selectedSubject = React.useMemo(
    () =>
      content?.body.subjects && selectedSubjectIndex !== undefined
        ? content.body.subjects[selectedSubjectIndex]
        : undefined,
    [content?.body.subjects, selectedSubjectIndex],
  );

  const handleOrderGuestFormSucess = React.useCallback((foundedOrder: OrderApiDto) => {
    setOrder(foundedOrder);
    setSatisfied(false);
  }, []);

  const handleCustomerOrderFormChange = React.useCallback(
    (values: CustomerOrderFormValues) => {
      setSearchParams({});

      if (values.id === '') {
        setOrder(undefined);
      } else {
        const customerOrder = customerOrders?.find((_customerOrder) => _customerOrder.id === values.id);
        customerOrder &&
          getOrderByCodeAndEmail(
            customerOrder.code,
            customerOrder.email,
            {
              populate: orderPopulate,
            },
            { silent: false },
          ).then((response) => {
            setOrder(response.data);
          });
      }

      setSatisfied(false);
    },
    [customerOrders, getOrderByCodeAndEmail, setSearchParams],
  );

  const handleSatisfiedFormChange = React.useCallback((values: SatisfiedFormValues) => {
    setSatisfied(getFormikBooleanAsStringValue(values.satisfied));
  }, []);

  React.useEffect(() => {
    const subjectCodeFromGet = searchParams.get('CC');
    const satisfiedFromGet = searchParams.get('satisfied');
    const orderCodeFromGet = searchParams.get('orderCode');
    const orderFinded = customerOrders?.find((_order) => _order.code === orderCodeFromGet);

    if (subjectCodeFromGet) {
      const _selectedSubjectIndex = content?.body.subjects?.findIndex((subject) =>
        subject.subject?.includes(`[${subjectCodeFromGet}]`),
      );
      setSelectedSubjectIndex(_selectedSubjectIndex);
    }

    if (satisfiedFromGet) {
      setSatisfied(satisfiedFromGet === 'false' ? false : true);
    }

    if (customerOrders && orderCodeFromGet && orderFinded) {
      const _selectedCustomerOrderIndex = customerOrders.findIndex((_order) => _order.code === orderCodeFromGet);
      setSelectedCustomerOrderIndex(_selectedCustomerOrderIndex);
      getOrderByCodeAndEmail(orderFinded.code, orderFinded.email, { populate: orderPopulate }, { silent: false }).then(
        (response) => {
          setOrder(response.data);
        },
      );
    }
  }, [content?.body.subjects, customerOrders, getOrderByCodeAndEmail, searchParams]);

  React.useEffect(() => {
    if (selectedSubject?.subject?.startsWith('[CC21]')) {
      setIsRightOfWithdrawal(true);
    } else {
      setIsRightOfWithdrawal(false);
    }
  }, [selectedSubject?.subject]);

  // Se a navigare è un customer loggato, prendo la lista dei suoi ordini
  React.useEffect(() => {
    if (session?.userLogged) {
      getOrders().then((response) => {
        // Nel caso in cui la domanda è il diritto di recesso filtro gli ordini
        if (isRightOfWithdrawal) {
          setCustomerOrders(
            response.data.filter((customerOrder) => {
              const rightOfWithdrawalInDate = customerOrder.withdrawalEndDate
                ? new Date(customerOrder.withdrawalEndDate) > new Date()
                : false;

              return rightOfWithdrawalInDate;
            }),
          );
        } else {
          setCustomerOrders(response.data);
        }
      });
    }
  }, [getOrders, isRightOfWithdrawal, session?.userLogged]);

  React.useEffect(() => {
    const rightOfWithdrawalInDate = order?.withdrawalEndDate ? new Date(order.withdrawalEndDate) > new Date() : false;
    if (isRightOfWithdrawal) {
      if (session?.userLogged) {
        if (customerOrders && customerOrders.length > 0) {
          setForceCustomerCareForm(true);
          setSatisfied(false);
        } else {
          setForceCustomerCareForm(false);
          setSatisfied(true);
        }
      } else {
        if (order) {
          if (rightOfWithdrawalInDate) {
            setForceCustomerCareForm(true);
            setSatisfied(false);
          } else {
            setForceCustomerCareForm(false);
            setSatisfied(true);
          }
        } else {
          setForceCustomerCareForm(true);
          setSatisfied(false);
        }
      }
    }
  }, [customerOrders, isRightOfWithdrawal, order, session?.userLogged]);

  return (
    <Div className={classnames({ 'is-right-of-withdrawal': isRightOfWithdrawal })} marginBottom={-4}>
      {content && (
        <Div marginBottom={4}>
          <SubjectForm content={content} onChange={handleSubjectFormChange} preselectedIndex={selectedSubjectIndex} />
        </Div>
      )}
      {selectedSubject && (
        <>
          <Div marginBottom={4}>{formatHtml(selectedSubject.message)}</Div>
          {selectedSubject.orderRelated === true && (
            <Div marginBottom={4}>
              {session?.userLogged ? (
                <React.Fragment>
                  {customerOrders && customerOrders.length > 0 && (
                    <CustomerOrderForm
                      customerOrders={customerOrders}
                      onChange={handleCustomerOrderFormChange}
                      preselectedIndex={selectedCustomerOrderIndex}
                    />
                  )}
                </React.Fragment>
              ) : (
                <OrderGuestForm onSuccess={handleOrderGuestFormSucess} orderPopulate={orderPopulate} />
              )}
            </Div>
          )}
          {order && (
            <Div marginBottom={4}>
              <OrderDetails order={order} />
            </Div>
          )}
          {(forceCustomerCareForm || !disableSatisfiedForm) && (
            <>
              {satisfied !== false && !isRightOfWithdrawal && (
                <>
                  {showSatisfiedForm ? (
                    <P>{t('contactUs.notSatisfied.label')}</P>
                  ) : (
                    <Div marginBottom={4}>
                      <SatisfiedForm onChange={handleSatisfiedFormChange} satisfied={satisfied} />
                    </Div>
                  )}
                </>
              )}
              {(satisfied === false || showSatisfiedForm) && (
                <Div marginBottom={4}>
                  <CustomerCareForm
                    initialValues={{
                      email: session?.customer?.email ?? order?.email ?? undefined,
                      message: order ? `${t('misc.orderCode')}: ${order.code}\n` : undefined,
                      name: session?.customer?.name ?? order?.billingAddress?.name ?? undefined,
                      phoneNumber: session?.customer?.phoneNumbers?.at(0) ?? order?.billingAddress?.phone ?? undefined,
                      surname: session?.customer?.surname ?? order?.billingAddress?.surname ?? undefined,
                    }}
                    key={`${selectedSubject.subject}-${order?.id}`}
                    subject={selectedSubject.subject}
                  />
                </Div>
              )}
            </>
          )}
          {isRightOfWithdrawal && session?.userLogged && customerOrders && customerOrders.length === 0 && (
            <P>
              <strong>
                <u>{t('customerCare.rightOfWithdrawal.noOrders')}</u>
              </strong>
            </P>
          )}
        </>
      )}
    </Div>
  );
};

interface SubjectFormProps {
  content?: ContentApiDto<ContactUsContentType>;
  onChange(values: SubjectFormValues): void;
  preselectedIndex?: number;
}

interface SubjectFormValues {
  subject: formikString;
}

const SubjectForm = ({ content, onChange, preselectedIndex }: SubjectFormProps) => {
  const preselectedSubject = React.useMemo(() => {
    return preselectedIndex?.toString() ?? getInitialString();
  }, [preselectedIndex]);

  return (
    <Form<SubjectFormValues>
      initialValues={{ subject: preselectedSubject }}
      key={preselectedSubject}
      onChange={onChange}
    >
      <FormGroup label={t('contactUs.subject.label')} names={['subject']}>
        <Select includeEmptyOption={false} name="subject" placeholder={t('contactUs.subject.placeholder')}>
          {content?.body.subjects &&
            content.body.subjects.map(({ contentSectionId, subject }, index) => (
              <option key={contentSectionId} value={index}>
                {subject}
              </option>
            ))}
        </Select>
      </FormGroup>
    </Form>
  );
};

interface CustomerOrderFormProps {
  customerOrders?: Array<OrderApiDto>;
  onChange(values: CustomerOrderFormValues): void;
  preselectedIndex?: number;
}

interface CustomerOrderFormValues {
  id: formikString;
}

const CustomerOrderForm = ({ customerOrders, onChange, preselectedIndex }: CustomerOrderFormProps) => {
  const preselectedOrderId = React.useMemo(() => {
    return preselectedIndex !== undefined
      ? (customerOrders?.[preselectedIndex]?.id ?? getInitialString())
      : getInitialString();
  }, [customerOrders, preselectedIndex]);
  return (
    <Form<CustomerOrderFormValues>
      initialValues={{ id: preselectedOrderId }}
      key={preselectedOrderId}
      onChange={onChange}
    >
      <FormGroup label={t('contactUs.customerOrder.label')} names={['id']}>
        <Select includeEmptyOption={false} name="id" placeholder={t('contactUs.customerOrder.placeholder')}>
          {customerOrders &&
            customerOrders.map((customerOrder) => (
              <option key={customerOrder.id} value={customerOrder.id}>
                {customerOrder.code}
              </option>
            ))}
        </Select>
      </FormGroup>
    </Form>
  );
};

interface SatisfiedFormProps {
  onChange(values: SatisfiedFormValues): void;
  satisfied?: boolean;
}

interface SatisfiedFormValues {
  satisfied: formikBooleanAsString;
}

const SatisfiedForm = ({ onChange, satisfied = true }: SatisfiedFormProps) => {
  return (
    <Form<SatisfiedFormValues>
      initialValues={{ satisfied: getInitialBooleanAsString(satisfied) }}
      key={String(satisfied)}
      onChange={onChange}
    >
      <fieldset>
        <legend className="form-label">{t('contactUs.satisfied.label')}</legend>
        <FormGroup label={undefined} names={['satisfied']} omitForAttribute>
          <Radio id="yes" inline label={t('contactUs.satisfied.yes')} name="satisfied" value="true" />
          <Radio id="no" inline label={t('contactUs.satisfied.no')} name="satisfied" value="false" />
        </FormGroup>
      </fieldset>
    </Form>
  );
};

const orderPopulate: OrderPopulate = {
  billingAddress: true,
  paymentMethod: true,
  shippingAddress: true,
  warehouseOrders: { carrierTracking: true, orderedSkus: { sku: { product: true } } },
};
